import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronDown, ChevronUp, Clock, AlertCircle, Mail } from 'lucide-react';
export default function Cancellation() {
  const [openSections, setOpenSections] = useState({
    standard: true,
    court: true,
    howToCancel: true
  });
  const toggleSection = section => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  return <main className="min-h-screen bg-white" data-id="element-572">
      <Helmet data-id="element-573">
        <title data-id="element-574">Cancellation Policy | Medicolegal Psychiatry</title>
        <meta name="description" content="Cancellation and rescheduling policy for Medicolegal Psychiatry services, including guidelines for standard appointments and court attendance." data-id="element-575" />
        <meta name="keywords" content="Cancellation Policy, Appointment Cancellation, Court Attendance, Medicolegal Psychiatry" data-id="element-576" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white py-16 pt-24 md:pt-28" data-id="element-577">
        <div className="container mx-auto px-4" data-id="element-578">
          <div className="flex items-center justify-center mb-4" data-id="element-579">
            <Clock className="h-12 w-12 mr-4" data-id="element-580" />
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold" data-id="element-581">
              Cancellation and Rescheduling Policy
            </h1>
          </div>
        </div>
      </section>
      {/* Policy Content */}
      <section className="py-16" data-id="element-582">
        <div className="container mx-auto px-4" data-id="element-583">
          <div className="max-w-3xl mx-auto" data-id="element-584">
            <p className="text-slate-dark text-lg mb-8" data-id="element-585">
              This policy is designed to ensure the efficient management of our
              professional commitments and to provide clear guidelines for all
              parties.
            </p>
            <div className="space-y-4 mb-8" data-id="element-586">
              {/* Standard Appointments Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-587">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.standard ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('standard')} data-id="element-588">
                  <div className="flex items-center" data-id="element-589">
                    <Clock className="h-5 w-5 mr-2" data-id="element-590" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-591">
                      Standard Appointments and Assessments
                    </h2>
                  </div>
                  {openSections.standard ? <ChevronUp className="h-5 w-5" data-id="element-592" /> : <ChevronDown className="h-5 w-5" data-id="element-593" />}
                </button>
                {openSections.standard && <div className="p-6 bg-white" data-id="element-594">
                    <p className="text-slate-dark mb-4" data-id="element-595">
                      Due to the significant time allocated for each assessment,
                      including preparation and report writing, our cancellation
                      policy is strictly enforced to maintain the quality of
                      service for all clients.
                    </p>
                    <ul className="space-y-3 text-slate-dark" data-id="element-596">
                      <li className="flex items-start" data-id="element-597">
                        <AlertCircle className="h-5 w-5 text-primary mr-2 mt-1" data-id="element-598" />
                        <div data-id="element-599">
                          <strong data-id="element-600">More than 72 hours' notice:</strong> There
                          will be no charge for appointments cancelled with more
                          than 72 hours' notice.
                        </div>
                      </li>
                      <li className="flex items-start" data-id="element-601">
                        <AlertCircle className="h-5 w-5 text-primary mr-2 mt-1" data-id="element-602" />
                        <div data-id="element-603">
                          <strong data-id="element-604">Less than 72 hours' notice:</strong> The
                          instructing party will be charged for all preparation
                          work already undertaken at our Hourly Rate, plus any
                          incurred travel time and expenses.
                        </div>
                      </li>
                      <li className="flex items-start" data-id="element-605">
                        <AlertCircle className="h-5 w-5 text-primary mr-2 mt-1" data-id="element-606" />
                        <div data-id="element-607">
                          <strong data-id="element-608">
                            Less than 24 hours' notice or a "no-show":
                          </strong>{' '}
                          The full fee for the scheduled assessment may be
                          charged, as this time cannot be reallocated to other
                          clients.
                        </div>
                      </li>
                    </ul>
                  </div>}
              </div>
              {/* Court Attendance Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-609">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.court ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('court')} data-id="element-610">
                  <div className="flex items-center" data-id="element-611">
                    <AlertCircle className="h-5 w-5 mr-2" data-id="element-612" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-613">
                      Court and Tribunal Attendance
                    </h2>
                  </div>
                  {openSections.court ? <ChevronUp className="h-5 w-5" data-id="element-614" /> : <ChevronDown className="h-5 w-5" data-id="element-615" />}
                </button>
                {openSections.court && <div className="p-6 bg-white" data-id="element-616">
                    <p className="text-slate-dark mb-4" data-id="element-617">
                      The cancellation of court or tribunal attendance with
                      short notice can result in substantial costs and is
                      subject to separate terms.
                    </p>
                    <ul className="space-y-3 text-slate-dark" data-id="element-618">
                      <li className="flex items-start" data-id="element-619">
                        <AlertCircle className="h-5 w-5 text-primary mr-2 mt-1" data-id="element-620" />
                        <div data-id="element-621">
                          <strong data-id="element-622">More than 7 working days' notice:</strong>{' '}
                          There will be no charge for the attendance fee. Any
                          preparation work already completed will be billed at
                          the Hourly Rate.
                        </div>
                      </li>
                      <li className="flex items-start" data-id="element-623">
                        <AlertCircle className="h-5 w-5 text-primary mr-2 mt-1" data-id="element-624" />
                        <div data-id="element-625">
                          <strong data-id="element-626">Less than 7 working days' notice:</strong> The
                          full fee for court attendance will be charged, in
                          addition to all preparation work, travel time, and
                          expenses.
                        </div>
                      </li>
                    </ul>
                  </div>}
              </div>
              {/* How to Cancel Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-627">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.howToCancel ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('howToCancel')} data-id="element-628">
                  <div className="flex items-center" data-id="element-629">
                    <Mail className="h-5 w-5 mr-2" data-id="element-630" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-631">
                      How to Cancel
                    </h2>
                  </div>
                  {openSections.howToCancel ? <ChevronUp className="h-5 w-5" data-id="element-632" /> : <ChevronDown className="h-5 w-5" data-id="element-633" />}
                </button>
                {openSections.howToCancel && <div className="p-6 bg-white" data-id="element-634">
                    <p className="text-slate-dark mb-4" data-id="element-635">
                      All cancellations or requests to reschedule must be made
                      in writing via email to
                      enquiries@medicolegal-psychiatry.co.uk. The cancellation
                      will be deemed effective from the time and date of receipt
                      of the email.
                    </p>
                    <p className="text-slate-dark" data-id="element-636">
                      We understand that unforeseen circumstances can arise and
                      will consider exceptions on a case-by-case basis.
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}